import { trafficPartPrefix, trafficMatchingPrefix } from '../trafficQuestionNumbering';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import { trainers } from '../constants';
import { sanitizeTrafficCourseTasks } from '../trafficQuestionSchema';
import {
  getTaskState,
  getTheoryReviewTargets,
  getTrafficStageStatusLabel,
  hasAutomaticAnswerKey,
  STUDENT_DECLARATION_TYPE,
  buildTrafficReviewState,
} from '../trafficCourseFlowUtils';
import { clearPageMessage, showLoadingMessage, showSuccessMessage, showWarningMessage } from '../successMessage';

// Re-exported so existing trainer-side imports keep one source of truth.
export { getTheoryReviewTargets };

const boxStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '16px',
  background: '#fff',
  marginBottom: '14px',
};

const inputStyle = {
  width: '100%',
  border: '1px solid #cbd5e1',
  borderRadius: '10px',
  padding: '10px 12px',
  boxSizing: 'border-box',
  fontFamily: "'Rubik', sans-serif",
  fontSize: '14px',
};

const actionButtonStyle = (primary = false) => ({
  minHeight: '40px',
  border: `1px solid ${primary ? '#111827' : '#cbd5e1'}`,
  borderRadius: '10px',
  background: primary ? '#111827' : '#fff',
  color: primary ? '#fff' : '#111827',
  padding: '10px 16px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
});

const markButtonStyle = (active, variant = 'default') => {
  const palette = variant === 'pass'
    ? { border: '#16a34a', background: '#f0fdf4', color: '#166534' }
    : variant === 'fail'
      ? { border: '#dc2626', background: '#fef2f2', color: '#991b1b' }
      : { border: '#cbd5e1', background: '#fff', color: '#334155' };

  return {
    minWidth: '48px',
    minHeight: '32px',
    border: `${active ? 2 : 1}px solid ${active ? '#111827' : palette.border}`,
    borderRadius: '8px',
    background: palette.background,
    color: palette.color,
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
  };
};

const getTrainerSignatureAssetPath = (trainerId = '') => {
  const trainer = trainers.find((row) => row.trainerId === trainerId);
  const filename = trainer?.sign?.split('/').pop();
  return filename ? `/trainer-signatures/${filename}` : '';
};

export const getActivityChecklist = (task) => {
  const entries = task.assessorChecklist || [];
  return entries.some(entry => !['guide', 'subsection'].includes(entry.kind || 'criterion'))
    ? entries : [...entries, { id: 'overall-activity-outcome', kind: 'criterion', text: 'Overall activity outcome' }];
};

const MarkButtons = ({ value = '', onChange }) => (
  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
    <button type='button' onClick={() => onChange('C')} style={markButtonStyle(value === 'C', 'pass')}>C</button>
    <button type='button' onClick={() => onChange('NYC')} style={markButtonStyle(value === 'NYC', 'fail')}>NYC</button>
  </div>
);

const optionLetter = (index) => String.fromCharCode(97 + index);

// Existing diagram prompts sometimes use a short-answer response type while
// storing a real option pool. The student flow treats these as choices, so the
// trainer view must do the same for both display and auto-mark review targets.
const isOptionBackedTextResponse = (question = {}) => (
  question.responseType === 'short-answer'
  && Array.isArray(question.options)
  && question.options.filter((option) => String(option || '').trim()).length >= 2
  && hasAutomaticAnswerKey(question)
);

const ReadOnlyQuestion = ({
  question,
  value,
  reviewEnabled = false,
  getMarkValue = () => '',
  getAutoMarkValue = () => '',
  onMarkChange,
  isQuestionReviewable = () => false,
  isPartReviewable = () => false,
}) => {
  const getOptionLabel = (prompt, selectedValue) => {
    if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
      return '';
    }

    const options = prompt?.options || [];
    const optionIndex = Number(selectedValue);
    if (Number.isInteger(optionIndex) && optionIndex >= 0 && optionIndex < options.length) {
      const optionText = options[optionIndex] || `Option ${optionIndex + 1}`;
      return `${optionLetter(optionIndex)}. ${optionText}`;
    }

    return String(selectedValue);
  };

  const renderValue = (entry, prompt = question) => {
    if (Array.isArray(entry)) {
      const renderedEntries = entry.map((item) => getOptionLabel(prompt, item)).filter(Boolean);
      return renderedEntries.length > 0 ? renderedEntries.join(', ') : '—';
    }

    if (prompt?.type === 'matching' && entry && typeof entry === 'object') {
      return (prompt.matchingPairs || []).map((pair, index) => (
        <div key={pair.id} style={{ marginTop: '6px' }}>
          <strong>{trafficMatchingPrefix(prompt, index)}{pair.left}</strong>: <span>{String(entry[pair.id] || '—')}</span>
        </div>
      ));
    }

    if (prompt?.type === 'ordering' && entry && typeof entry === 'object') {
      return [...(prompt.orderingItems || [])]
        .sort((left, right) => Number(entry[left.id] || 0) - Number(entry[right.id] || 0))
        .map((item, index) => <div key={item.id} style={{ marginTop: '6px' }}><strong>{index + 1}.</strong> {item.text}</div>);
    }

    if (entry && typeof entry === 'object') {
      return Object.entries(entry).map(([key, nested]) => (
        <div key={key} style={{ marginTop: '6px' }}>
          <strong>{key}</strong>: <span>{String(nested || '')}</span>
        </div>
      ));
    }

    if (['multiple-choice', 'image-multiple-choice', 'true-false', 'yes-no', 'multiple-response'].includes(prompt?.responseType || prompt?.type) || isOptionBackedTextResponse(prompt)) {
      return <span>{getOptionLabel(prompt, entry) || '—'}</span>;
    }

    return <span>{String(entry || '') || '—'}</span>;
  };

  if (question.type === 'student-guide') {
    return (
      <div style={{ ...boxStyle, background: '#f8fafc' }}>
        <strong style={{ display: 'block', marginBottom: '8px' }}>Guide / Reference</strong>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{question.text}</p>
      </div>
    );
  }

  return (
    <div style={boxStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '8px' }}>
        <strong style={{ display: 'block' }}>{question.numberLabel}. {question.text}</strong>
        {getAutoMarkValue(question.id) && (
          <span style={{ flexShrink: 0, padding: '6px 9px', border: `1px solid ${getAutoMarkValue(question.id) === 'C' ? '#a7d8b6' : '#f3b35b'}`, borderRadius: '999px', background: getAutoMarkValue(question.id) === 'C' ? '#edf8f0' : '#fff1dc', color: getAutoMarkValue(question.id) === 'C' ? '#287a45' : '#92400e', fontSize: '12px', fontWeight: 700 }}>
            Auto-marked: {getAutoMarkValue(question.id)}
          </span>
        )}
        {reviewEnabled && onMarkChange && isQuestionReviewable(question.id) && (
          <MarkButtons value={getMarkValue(question.id)} onChange={(mark) => onMarkChange(question.id, mark)} />
        )}
      </div>
      {question.mediaUrl && <img src={question.mediaUrl} alt="Question reference" style={{ maxWidth: '100%', maxHeight: '360px', objectFit: 'contain', marginBottom: '12px' }} />}
      {question.subQuestions?.length ? (
        <div style={{ display: 'grid', gap: '12px' }}>
          {question.subQuestions.map((part, index) => (
            <div key={part.id} style={{ border: '1px solid #edf2f7', borderRadius: '10px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                <strong>{trafficPartPrefix(question, index)}{part.text}</strong>
                {reviewEnabled && onMarkChange && isPartReviewable(question.id, part.id) && (
                  <MarkButtons value={getMarkValue(question.id, part.id)} onChange={(mark) => onMarkChange(question.id, mark, part.id)} />
                )}
              </div>
              {part.mediaUrl && <img src={part.mediaUrl} alt={`Question part ${String.fromCharCode(97 + index)} reference`} style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain', marginTop: '8px' }} />}
              <div style={{ marginTop: '8px' }}>{renderValue(value?.[part.id], part)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div>{renderValue(value, question)}</div>
      )}
    </div>
  );
};

const TrafficCourseStageViewer = ({ flowDoc, taskId, userData, onFlowUpdated, onClose }) => {
  const [liveFlowDoc, setLiveFlowDoc] = useState(flowDoc || null);
  const [trainerDeclarationDraft, setTrainerDeclarationDraft] = useState({
    trainerName: '',
    declarationDate: '',
    signature: '',
    comments: '',
  });
  const [assessorReviewDraft, setAssessorReviewDraft] = useState({
    questionMarks: {},
    checklistMarks: {},
  });
  const [assessorReviewDirty, setAssessorReviewDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [headerReviewActionsTarget, setHeaderReviewActionsTarget] = useState(null);

  useEffect(() => {
    setHeaderReviewActionsTarget(document.getElementById('traffic-stage-header-review-actions'));
  }, []);

  useEffect(() => {
    if (!flowDoc) {
      setLiveFlowDoc(null);
      return;
    }

    setLiveFlowDoc((current) => {
      if (!current || current.id !== flowDoc.id) {
        return flowDoc;
      }

      const currentUpdatedAt = new Date(current.updatedAt || current.createdAt || 0).getTime();
      const nextUpdatedAt = new Date(flowDoc.updatedAt || flowDoc.createdAt || 0).getTime();

      return nextUpdatedAt >= currentUpdatedAt ? flowDoc : current;
    });
  }, [flowDoc]);

  useEffect(() => {
    if (!flowDoc?.id) {
      return undefined;
    }

    const unsubscribe = onSnapshot(doc(db, 'trafficCourseFlows', flowDoc.id), (snapshot) => {
      if (!snapshot.exists()) {
        return;
      }

      const nextFlowDoc = { id: snapshot.id, ...snapshot.data() };
      setLiveFlowDoc(nextFlowDoc);
      onFlowUpdated && onFlowUpdated(nextFlowDoc);
    });

    return () => unsubscribe();
  }, [flowDoc?.id, onFlowUpdated]);

  const task = useMemo(() => sanitizeTrafficCourseTasks((liveFlowDoc?.tasks || []).filter(row => row.id === taskId))[0] || null, [liveFlowDoc?.tasks, taskId]);
  const taskState = getTaskState(liveFlowDoc || {}, taskId);
  const taskResponse = liveFlowDoc?.responses?.[taskId] || {};
  const pdfCompletionReady = ['Theory Questions', 'Student Declaration', 'Trainer/Assessor Declaration']
    .every(type => (liveFlowDoc?.tasks || []).some(row => row.type === type))
    && (liveFlowDoc?.tasks || [])
      .filter(row => ['Theory Questions', 'Activity', 'Student Declaration', 'Trainer/Assessor Declaration'].includes(row.type))
      .every(row => liveFlowDoc?.taskStates?.[row.id]?.status === 'completed');
  const fallbackTrainerName = useMemo(() => (
    trainers.find((row) => row.trainerId === (liveFlowDoc?.trainerId || userData?.trainerId))?.name
      || userData?.name
      || userData?.trainerId
      || ''
  ), [liveFlowDoc?.trainerId, userData]);
  const fallbackTrainerSignature = useMemo(() => (
    taskResponse.signature || getTrainerSignatureAssetPath(liveFlowDoc?.trainerId || userData?.trainerId || '')
  ), [liveFlowDoc?.trainerId, taskResponse.signature, userData]);
  const reviewEnabled = !taskState?.priorCompletion && ['Theory Questions', 'Activity'].includes(task?.type) && ['submitted', 'completed'].includes(taskState?.status);
  const canSubmitManually = !taskState?.priorCompletion
    && ['Theory Questions', 'Activity'].includes(task?.type)
    && ['locked', 'available', 'pending', undefined, null, ''].includes(taskState?.status);
  const canTrainerCompleteTask = task?.type !== 'Trainer/Assessor Declaration'
    && task?.type !== STUDENT_DECLARATION_TYPE
    && task?.type !== 'Student instructions'
    && task?.type !== 'Conditions of assessment'
    && taskState?.status === 'submitted';
  const theoryReviewTargets = useMemo(() => (
    task?.type === 'Theory Questions' ? getTheoryReviewTargets(task.questions || []) : []
  ), [task]);
  const reviewChecklistEntries = useMemo(() => (
    task?.type === 'Activity'
      ? getActivityChecklist(task).filter((entry) => !['guide', 'subsection'].includes(entry.kind || 'criterion'))
      : []
  ), [task]);
  const allTheoryQuestionsMarked = theoryReviewTargets.every((target) => ['C', 'NYC'].includes(assessorReviewDraft.questionMarks?.[target.id]));
  const allTheoryQuestionsPassed = theoryReviewTargets.every((target) => assessorReviewDraft.questionMarks?.[target.id] === 'C');
  const allChecklistEntriesMarked = reviewChecklistEntries.every((entry) => ['C', 'NYC'].includes(assessorReviewDraft.checklistMarks?.[entry.id]));
  const allChecklistEntriesPassed = reviewChecklistEntries.every((entry) => assessorReviewDraft.checklistMarks?.[entry.id] === 'C');

  useEffect(() => {
    if (task?.type !== 'Trainer/Assessor Declaration') {
      return;
    }

    setTrainerDeclarationDraft({
      trainerName: taskResponse.trainerName || fallbackTrainerName,
      declarationDate: taskResponse.declarationDate || new Date().toISOString().slice(0, 10),
      signature: fallbackTrainerSignature,
      comments: taskResponse.comments || '',
    });
    setInlineError('');
  }, [fallbackTrainerName, fallbackTrainerSignature, task?.type, taskResponse.comments, taskResponse.declarationDate, taskResponse.trainerName]);

  useEffect(() => {
    if (assessorReviewDirty) {
      return;
    }

    setAssessorReviewDraft({
      questionMarks: taskResponse.assessorReview?.questionMarks || {},
      checklistMarks: taskResponse.assessorReview?.checklistMarks || {},
    });
  }, [assessorReviewDirty, taskId, taskResponse.assessorReview]);

  useEffect(() => {
    setAssessorReviewDirty(false);
    setAssessorReviewDraft({
      questionMarks: taskResponse.assessorReview?.questionMarks || {},
      checklistMarks: taskResponse.assessorReview?.checklistMarks || {},
    });
  }, [liveFlowDoc?.id, taskId]);

  if (!task) {
    return <div style={boxStyle}>This section could not be found.</div>;
  }

  const updateFlowDoc = async (nextFlowDoc, loadingText, successText, failureText, errorLogLabel) => {
    setSaving(true);
    setInlineError('');
    showLoadingMessage(loadingText);

    try {
      await updateDoc(doc(db, 'trafficCourseFlows', nextFlowDoc.id), {
        responses: nextFlowDoc.responses,
        taskStates: nextFlowDoc.taskStates,
        updatedAt: nextFlowDoc.updatedAt,
        ...(nextFlowDoc.manualSubmissions ? { manualSubmissions: nextFlowDoc.manualSubmissions } : {}),
      });
      setLiveFlowDoc(nextFlowDoc);
      onFlowUpdated && onFlowUpdated(nextFlowDoc);
      setAssessorReviewDirty(false);
      setSaving(false);
      showSuccessMessage(successText);
      return true;
    } catch (saveError) {
      clearPageMessage();
      setSaving(false);
      setInlineError(failureText);
      showWarningMessage(failureText);
      console.error(errorLogLabel, saveError);
      return false;
    }
  };

  const handleSubmitManually = async () => {
    if (!canSubmitManually || saving || !liveFlowDoc?.id) return;
    const recordedAt = new Date().toISOString();
    const manualSubmission = {
      recordedAt,
      recordedBy: auth.currentUser?.uid || '',
      recordedByName: userData?.name || userData?.trainerId || '',
    };
    await updateFlowDoc({
      ...liveFlowDoc,
      manualSubmissions: { ...liveFlowDoc.manualSubmissions, [task.id]: manualSubmission },
      taskStates: { ...liveFlowDoc.taskStates, [task.id]: {
        ...taskState, status: 'submitted', submittedAt: recordedAt, returnedForCorrection: false,
      } },
      updatedAt: recordedAt,
    }, 'Recording manual submission', 'Manual submission recorded. Trainer marking is still required.',
    'Manual submission could not be saved. Please try again.', 'Unable to record manual traffic submission');
  };

  const handleSaveAssessorReview = async () => {
    if (!liveFlowDoc?.id || !reviewEnabled) {
      return;
    }

    const returned = buildTrafficReviewState(liveFlowDoc, task, assessorReviewDraft);
    const nextFlowDoc = {
      ...liveFlowDoc,
      responses: {
        ...(liveFlowDoc.responses || {}),
        [task.id]: {
          ...taskResponse,
          assessorRetryQuestionIds: returned.questionIds,
          assessorReview: {
            questionMarks: assessorReviewDraft.questionMarks || {},
            checklistMarks: assessorReviewDraft.checklistMarks || {},
          },
        },
      },
      taskStates: returned.states,
      updatedAt: new Date().toISOString(),
    };

    await updateFlowDoc(
      nextFlowDoc,
      'Saving Review',
      'Assessor review saved',
      'Assessor review could not be saved. Please try again.',
      'Unable to save traffic assessor review',
    );
  };

  const handleMarkTaskCompleted = async () => {
    if (!liveFlowDoc?.id || !task?.id || taskState?.status !== 'submitted') {
      return;
    }

    if (task.type === 'Theory Questions') {
      if (!allTheoryQuestionsMarked) {
        setInlineError('Mark each theory question before completing this section.');
        return;
      }
      if (!allTheoryQuestionsPassed) {
        setInlineError('All theory questions must be marked C before completing this section.');
        return;
      }
    }

    if (task.type === 'Activity') {
      if (!allChecklistEntriesMarked) {
        setInlineError('Mark each activity requirement before completing this activity.');
        return;
      }
      if (!allChecklistEntriesPassed) {
        setInlineError('All activity requirements must be marked C before completing this activity.');
        return;
      }
    }

    const nextFlowDoc = {
      ...liveFlowDoc,
      responses: {
        ...(liveFlowDoc.responses || {}),
        [task.id]: {
          ...taskResponse,
          assessorReview: {
            questionMarks: assessorReviewDraft.questionMarks || {},
            checklistMarks: assessorReviewDraft.checklistMarks || {},
          },
        },
      },
      taskStates: {
        ...(liveFlowDoc.taskStates || {}),
        [task.id]: {
          ...(liveFlowDoc.taskStates?.[task.id] || {}),
          status: 'completed',
          submittedAt: liveFlowDoc.taskStates?.[task.id]?.submittedAt || new Date().toISOString(),
          completedAt: new Date().toISOString(),
        },
      },
      updatedAt: new Date().toISOString(),
    };

    const activityNumberLabel = String(task.name || '').match(/Activity\s+\d+/i)?.[0] || 'Activity';
    const saved = await updateFlowDoc(
      nextFlowDoc,
      'Marking Section Completed',
      `${activityNumberLabel} - Marked as completed.`,
      'This section could not be marked as completed. Please try again.',
      'Unable to mark traffic section as completed',
    );
    if (saved) {
      onClose && onClose();
    }
  };

  const handleCompleteTrainerDeclaration = async () => {
    if (!liveFlowDoc?.id) {
      return;
    }

    if (!trainerDeclarationDraft.trainerName.trim() || !trainerDeclarationDraft.declarationDate || !trainerDeclarationDraft.signature) {
      setInlineError('Trainer name, date, and configured trainer signature are required.');
      return;
    }

    const nextFlowDoc = {
      ...liveFlowDoc,
      responses: {
        ...(liveFlowDoc.responses || {}),
        [task.id]: {
          ...taskResponse,
          trainerId: liveFlowDoc.trainerId || userData?.trainerId || '',
          trainerName: trainerDeclarationDraft.trainerName.trim(),
          declarationDate: trainerDeclarationDraft.declarationDate,
          signature: trainerDeclarationDraft.signature,
          comments: trainerDeclarationDraft.comments || '',
          completedBy: userData?.id || '',
          completedByName: userData?.name || userData?.trainerId || '',
        },
      },
      taskStates: {
        ...(liveFlowDoc.taskStates || {}),
        [task.id]: {
          ...(liveFlowDoc.taskStates?.[task.id] || {}),
          status: 'completed',
          submittedAt: liveFlowDoc.taskStates?.[task.id]?.submittedAt || new Date().toISOString(),
          completedAt: new Date().toISOString(),
        },
      },
      updatedAt: new Date().toISOString(),
    };

    await updateFlowDoc(
      nextFlowDoc,
      'Saving Trainer Declaration',
      'Trainer declaration saved',
      'Trainer declaration could not be saved. Please try again.',
      'Unable to save traffic trainer declaration',
    );
  };

  const handleAssessmentPdf = async (action) => {
    setGeneratingPdf(true);
    setInlineError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Please sign in again to access the assessment PDF.');
      const result = await fetch('https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/generateTrafficAssessmentPdf', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ flowId: liveFlowDoc.id, action }),
      });
      if (!result.ok) {
        const body = await result.json().catch(() => ({}));
        throw new Error(body.error || 'Unable to access assessment PDF. Please try again.');
      }
      const url = URL.createObjectURL(await result.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = liveFlowDoc.linkedCourse === 'traffic-implementer' ? 'Traffic-Implementer-Assessment.pdf' : 'Traffic-Controller-Assessment.pdf';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) { setInlineError(error.message); }
    finally { setGeneratingPdf(false); }
  };

  if (taskState?.priorCompletion) {
    const evidence = taskState.priorCompletion;
    return <div className="contentBlock">
      <h3><span aria-hidden="true">&#10003; </span>Completed - credited from Traffic Control</h3>
      <p>{task?.name} was completed in the Traffic Control course on {evidence.sourceCourseDate}.</p>
      <p>Answers and assessor evidence remain in the original assessment. This section does not require another submission or marking.</p>
      <p>Source assessment: <code>{evidence.sourceFlowId}</code></p>
      <p>Original completion: {evidence.sourceCompletedAt ? new Date(evidence.sourceCompletedAt).toLocaleString('en-AU') : evidence.sourceCourseDate}</p>
    </div>;
  }

  return (
    <div className="contentBlock">
      {canSubmitManually && <div style={boxStyle}>
        <p>Received this section on paper? Record it as submitted manually. This does not mark it satisfactory or replace saved online answers.</p>
        <button type="button" disabled={saving} style={actionButtonStyle(false)} onClick={handleSubmitManually}>
          Submitted manually
        </button>
      </div>}
      {liveFlowDoc.manualSubmissions?.[task.id] && <div style={boxStyle} role="status">
        Answers filled manually. Recorded {new Date(liveFlowDoc.manualSubmissions[task.id].recordedAt).toLocaleString('en-AU')}
        {liveFlowDoc.manualSubmissions[task.id].recordedByName ? ` by ${liveFlowDoc.manualSubmissions[task.id].recordedByName}` : ''}.
      </div>}
      {['traffic-controller', 'traffic-implementer'].includes(liveFlowDoc?.linkedCourse) && task.type === 'Trainer/Assessor Declaration' && (
        <div style={{ ...boxStyle, textAlign: 'right' }}>
          {liveFlowDoc.assessmentPdf?.storagePath && (
            <button type='button' disabled={saving || generatingPdf} style={{ ...actionButtonStyle(false), marginRight: '10px' }}
              onClick={() => handleAssessmentPdf('download')}>
              Download Assessment PDF
            </button>
          )}
          <button type='button' disabled={saving || generatingPdf || !pdfCompletionReady}
            title={pdfCompletionReady ? 'Download the completed assessment using the saved declarations.' : 'Complete all theory, activities and declarations first.'}
            style={{ ...actionButtonStyle(false), opacity: saving || generatingPdf || !pdfCompletionReady ? 0.55 : 1, cursor: saving || generatingPdf || !pdfCompletionReady ? 'not-allowed' : 'pointer' }}
            onClick={() => handleAssessmentPdf('generate')}>
            {generatingPdf ? 'Preparing PDF...' : liveFlowDoc.assessmentPdf?.storagePath ? 'Regenerate Assessment PDF' : 'Generate Assessment PDF'}
          </button>
          {liveFlowDoc.assessmentPdf?.generatedAt && <p style={{ marginBottom: 0, fontSize: '13px', color: '#64748b' }}>
            Saved PDF generated {new Date(liveFlowDoc.assessmentPdf.generatedAt).toLocaleString('en-AU')}. Regenerate to include changes saved since then.
          </p>}
        </div>
      )}
      {headerReviewActionsTarget && task.type === 'Activity' && reviewEnabled && createPortal(
        <div className="trafficStageHeaderReviewButtons">
          {canTrainerCompleteTask && (
            <button
              type='button'
              className='qldStageModalSave'
              onClick={handleMarkTaskCompleted}
              disabled={saving || !assessorReviewDirty || !allChecklistEntriesMarked || !allChecklistEntriesPassed}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>,
        headerReviewActionsTarget,
      )}
      <div className="formWrapperInContentBlock" style={{ width: '100%' }}>
        {task.type !== 'Activity' && (
          <div style={boxStyle}>
            <p style={{ margin: 0, color: '#475569' }}>Status: <strong>{getTrafficStageStatusLabel(taskState)}</strong></p>
          </div>
        )}

        {task.type === 'Student instructions' && (
          <div style={boxStyle}>
            <div dangerouslySetInnerHTML={{ __html: task.instructions || '<p>No instructions added.</p>' }} />
          </div>
        )}

        {task.type === 'Conditions of assessment' && (
          <div style={boxStyle}>
            <div dangerouslySetInnerHTML={{ __html: task.conditions || '<p>No conditions added.</p>' }} />
          </div>
        )}

        {task.type === 'Theory Questions' && (task.questions || []).map((question) => (
          <ReadOnlyQuestion
            key={question.id}
            question={question}
            value={taskResponse.answers?.[question.id]}
            reviewEnabled={reviewEnabled && question.type !== 'student-guide'}
            getMarkValue={(questionId, partId = '') => assessorReviewDraft.questionMarks?.[partId ? `${questionId}::${partId}` : questionId] || ''}
            getAutoMarkValue={(questionId) => taskResponse.autoMark?.questionMarks?.[questionId] || ''}
            isQuestionReviewable={(questionId) => theoryReviewTargets.some((target) => target.questionId === questionId && !target.partId)}
            isPartReviewable={(questionId, partId) => theoryReviewTargets.some((target) => target.questionId === questionId && target.partId === partId)}
            onMarkChange={(questionId, mark, partId = '') => {
              setAssessorReviewDirty(true);
              setAssessorReviewDraft((current) => ({
                ...current,
                questionMarks: {
                  ...(current.questionMarks || {}),
                  [partId ? `${questionId}::${partId}` : questionId]: mark,
                },
              }));
            }}
          />
        ))}

        {task.type === 'Theory Questions' && reviewEnabled && theoryReviewTargets.length > 0 && (
          <div style={{ ...boxStyle, display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button
              type='button'
              onClick={() => {
                setAssessorReviewDirty(true);
                setAssessorReviewDraft((current) => ({
                  ...current,
                  questionMarks: theoryReviewTargets.reduce((acc, target) => {
                    acc[target.id] = 'C';
                    return acc;
                  }, { ...(current.questionMarks || {}) }),
                }));
              }}
              disabled={saving}
              style={{ ...actionButtonStyle(false), opacity: saving ? 0.55 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              Mark All as C
            </button>
          </div>
        )}

        {task.type === 'Theory Questions' && reviewEnabled && (
          <div style={{ ...boxStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <div style={{ color: '#475569', fontSize: '14px' }}>
              {theoryReviewTargets.length > 0
                ? 'Mark each review item as C or NYC, then complete the section once all items are satisfactory.'
                : 'This theory section has been auto-marked. Review the responses if needed, then mark the section as completed.'}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {theoryReviewTargets.length > 0 && (
                <button type='button' onClick={handleSaveAssessorReview} disabled={saving} style={{ ...actionButtonStyle(false), opacity: saving ? 0.55 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Save Review'}
                </button>
              )}
              {canTrainerCompleteTask && (
                <button
                  type='button'
                  onClick={handleMarkTaskCompleted}
                  disabled={saving || (theoryReviewTargets.length > 0 && (!allTheoryQuestionsMarked || !allTheoryQuestionsPassed))}
                  style={{ ...actionButtonStyle(true), opacity: saving || (theoryReviewTargets.length > 0 && (!allTheoryQuestionsMarked || !allTheoryQuestionsPassed)) ? 0.55 : 1, cursor: saving || (theoryReviewTargets.length > 0 && (!allTheoryQuestionsMarked || !allTheoryQuestionsPassed)) ? 'not-allowed' : 'pointer' }}
                >
                  {saving ? 'Saving...' : 'Mark as Completed'}
                </button>
              )}
            </div>
          </div>
        )}

        {task.type === 'Activity' && (
          <>
            {task.instructions && <div style={boxStyle}><strong style={{ display: 'block', marginBottom: '8px' }}>Instructions</strong><div dangerouslySetInnerHTML={{ __html: task.instructions }} /></div>}
            {task.activityScenario && <div style={boxStyle}><strong style={{ display: 'block', marginBottom: '8px' }}>Scenario</strong><div dangerouslySetInnerHTML={{ __html: task.activityScenario }} /></div>}
            {getActivityChecklist(task).length > 0 && (
              <div style={boxStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                  <strong>Activity requirements</strong>
                  {reviewEnabled && reviewChecklistEntries.length > 0 && (
                    <button
                      type='button'
                      onClick={() => {
                        setAssessorReviewDirty(true);
                        setAssessorReviewDraft((current) => ({
                          ...current,
                          checklistMarks: reviewChecklistEntries.reduce((acc, entry) => {
                            acc[entry.id] = 'C';
                            return acc;
                          }, { ...(current.checklistMarks || {}) }),
                        }));
                      }}
                      disabled={saving}
                      style={{ ...actionButtonStyle(false), opacity: saving ? 0.55 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                    >
                      Mark All as C
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {getActivityChecklist(task).map((entry) => {
                    const entryKind = entry.kind || 'criterion';
                    const canMarkEntry = reviewEnabled && !['guide', 'subsection'].includes(entryKind);
                    return (
                      <div key={entry.id} style={{ border: '1px solid #edf2f7', borderRadius: '10px', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <div>
                          <strong>{entry.numberLabel || ''} {entry.text}</strong>
                        </div>
                        {canMarkEntry && (
                          <MarkButtons
                            value={assessorReviewDraft.checklistMarks?.[entry.id] || ''}
                            onChange={(mark) => {
                              setAssessorReviewDirty(true);
                              setAssessorReviewDraft((current) => ({
                                ...current,
                                checklistMarks: {
                                  ...(current.checklistMarks || {}),
                                  [entry.id]: mark,
                                },
                              }));
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {reviewEnabled && <div style={{ ...boxStyle, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type='button' onClick={handleSaveAssessorReview} disabled={saving || !assessorReviewDirty} style={actionButtonStyle(false)}>Save Review</button>
              {canTrainerCompleteTask && <button type='button' onClick={handleMarkTaskCompleted}
                disabled={saving || !allChecklistEntriesMarked || !allChecklistEntriesPassed} style={actionButtonStyle(true)}>Mark as Completed</button>}
            </div>}
          </>
        )}

        {task.type === STUDENT_DECLARATION_TYPE && (
          <>
            <div style={boxStyle}>
              <div dangerouslySetInnerHTML={{ __html: task.declarationContent || '<p>No declaration content added.</p>' }} />
            </div>
            <div style={boxStyle}>
              <p><strong>{task.declarationNameLabel || 'Student Name'}:</strong> {taskResponse.studentName || '—'}</p>
              <p><strong>{task.declarationDateLabel || 'Date'}:</strong> {taskResponse.declarationDate || '—'}</p>
              {taskResponse.signature && <img src={taskResponse.signature} alt="Student signature" style={{ width: '260px', maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: '10px' }} />}
            </div>
            {canTrainerCompleteTask && (
              <div style={{ ...boxStyle, display: 'flex', justifyContent: 'flex-end' }}>
                <button type='button' onClick={handleMarkTaskCompleted} disabled={saving} style={{ ...actionButtonStyle(true), opacity: saving ? 0.55 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Mark as Completed'}
                </button>
              </div>
            )}
          </>
        )}

        {task.type === 'Trainer/Assessor Declaration' && (
          <>
            <div style={boxStyle}>
              <div dangerouslySetInnerHTML={{ __html: task.declarationContent || '<p>No trainer / assessor declaration content added.</p>' }} />
            </div>
            <div style={boxStyle}>
              <div style={{ display: 'grid', gap: '14px' }}>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <strong>{task.declarationNameLabel || 'Trainer / Assessor Name'}</strong>
                  <input
                    value={trainerDeclarationDraft.trainerName}
                    onChange={(event) => setTrainerDeclarationDraft((current) => ({ ...current, trainerName: event.target.value }))}
                    style={inputStyle}
                    disabled={saving}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <strong>{task.declarationDateLabel || 'Date'}</strong>
                  <input
                    type='date'
                    value={trainerDeclarationDraft.declarationDate}
                    onChange={(event) => setTrainerDeclarationDraft((current) => ({ ...current, declarationDate: event.target.value }))}
                    style={inputStyle}
                    disabled={saving}
                  />
                </label>
                <div style={{ display: 'grid', gap: '6px' }}>
                  <strong>{task.declarationSignatureLabel || 'Trainer / Assessor Signature'}</strong>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', background: '#fff' }}>
                    {trainerDeclarationDraft.signature
                      ? <img src={trainerDeclarationDraft.signature} alt='Trainer signature' style={{ width: '260px', maxWidth: '100%', objectFit: 'contain' }} />
                      : <span style={{ color: '#64748b', fontSize: '14px' }}>Trainer signature is not configured.</span>}
                  </div>
                </div>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <strong>Comments</strong>
                  <textarea
                    value={trainerDeclarationDraft.comments}
                    onChange={(event) => setTrainerDeclarationDraft((current) => ({ ...current, comments: event.target.value }))}
                    rows={5}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    disabled={saving}
                  />
                </label>
                {inlineError && <div style={{ color: '#b91c1c', fontSize: '14px', fontWeight: 600 }}>{inlineError}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type='button'
                    onClick={handleCompleteTrainerDeclaration}
                    disabled={saving || taskState?.status === 'trainer_only'}
                    style={{ ...actionButtonStyle(true), opacity: saving || taskState?.status === 'trainer_only' ? 0.55 : 1, cursor: saving || taskState?.status === 'trainer_only' ? 'not-allowed' : 'pointer' }}
                  >
                    {saving ? 'Saving...' : taskState?.status === 'completed' ? 'Save Changes' : 'Mark as Completed'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {inlineError && task.type !== 'Trainer/Assessor Declaration' && (
          <div style={{ ...boxStyle, color: '#b91c1c', fontSize: '14px', fontWeight: 600 }}>{inlineError}</div>
        )}
      </div>
    </div>
  );
};

export default TrafficCourseStageViewer;
